import { useEffect, useRef } from 'react';

interface UseClickOutsideOptions {
    active?: boolean;
    safeSelectors?: string[]; // e.g. ['.drawer-click-exempt', '#some-id']
    closeOnEscape?: boolean;
}

export function useClickOutside<T extends HTMLElement>(
    callback: () => void,
    {
        active = true,
        safeSelectors = [],
        closeOnEscape = true,
    }: UseClickOutsideOptions = {},
) {
    const ref = useRef<T | null>(null);

    useEffect(() => {
        if (!active) return;

        function handleMouseDown(event: MouseEvent) {
            const target = event.target as HTMLElement;

            const clickedInside = ref.current?.contains(target);
            const clickedInSafeZone = safeSelectors.some((selector) =>
                target.closest(selector),
            );

            if (!clickedInside && !clickedInSafeZone) {
                callback();
            }
        }

        function handleKeyDown(event: KeyboardEvent) {
            if (event.key === 'Escape') {
                callback();
            }
        }

        document.addEventListener('mousedown', handleMouseDown);
        if (closeOnEscape) {
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
            if (closeOnEscape) {
                document.removeEventListener('keydown', handleKeyDown);
            }
        };
    }, [callback, active, safeSelectors, closeOnEscape]);

    return ref;
}
